import { useEffect, useRef, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { attachmentUrl, fetchAttachmentUrl } from '../attachments';
import type { ChatAttachment } from '../hooks/useSoteriaChat';
import { documentPreviewUrl } from '@/shared/documentPreviewUrl';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';

/** An attachment opened in place, over the conversation.
 *
 * Clicking a chip used to leave the app: a new tab, the file, and a trip back.
 * For glancing at the statement you just sent — which is most of why anyone
 * clicks — that is the whole task's worth of friction for a two-second look.
 *
 * The chip stays a real `<a>` underneath, and a modified click still opens the
 * tab (see AttachmentChip). This is an enhancement over that link, never a
 * replacement for it: no scripted popup, nothing to be blocked.
 *
 * BOTH kinds render from a blob URL rather than from the endpoint, and for a
 * PDF that is not a preference. The API sends `X-Frame-Options: DENY` (the
 * `security_headers` snippet in Swarm.Caddyfile, on the API subdomain), so a
 * browser refuses to frame the endpoint at all. A `blob:` URL is same-origin to
 * this page and carries no such header, so the built-in PDF viewer — zoom, page
 * navigation, print — comes free.
 */
export function AttachmentPreview({
  attachment,
  open,
  onOpenChange,
  /** The image's object URL, when the chip has already fetched one for its
   *  thumbnail. Passing it makes the preview instant instead of re-fetching
   *  bytes the page is holding. */
  preloadedUrl,
}: {
  attachment: ChatAttachment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preloadedUrl?: string | null;
}) {
  const isImage = attachment.mime_type.startsWith('image/');
  const [fetched, setFetched] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  // DERIVED, not seeded into state. This preview mounts with the chip — before
  // the thumbnail has loaded — so seeding `useState(preloadedUrl)` captured the
  // null that was there at first render and never saw the URL arrive. The image
  // was then fetched a second time on open, which is exactly what passing it in
  // was supposed to avoid.
  const url = fetched ?? preloadedUrl ?? null;

  // What THIS component created, so unmount can revoke it. `preloadedUrl`
  // belongs to the chip and is never revoked here — the chip releases it on its
  // own unmount, and revoking it would blank the thumbnail behind the preview.
  const createdUrl = useRef<string | null>(null);

  // Fetched when the preview opens, not when the chip renders: a transcript
  // full of PDFs must not pull every one of them down to draw itself.
  useEffect(() => {
    if (!open || url) return;
    let cancelled = false;
    fetchAttachmentUrl(attachment.id)
      .then((next) => {
        if (cancelled) {
          URL.revokeObjectURL(next);
          return;
        }
        createdUrl.current = next;
        setFetched(next);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [open, url, attachment.id]);

  // Revoked on UNMOUNT, not on close.
  //
  // Revoking when the dialog closed left `url` still pointing at the blob that
  // had just been freed, so the fetch was skipped on the next open and the
  // frame rendered a dead URL — the file appeared once and was broken every
  // time after. Holding it for the component's life also makes reopening
  // instant rather than re-downloading several megabytes.
  useEffect(
    () => () => {
      if (createdUrl.current) URL.revokeObjectURL(createdUrl.current);
    },
    [],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Wide and near-full-height: this is a viewer, not a form. The default
        // sm:max-w-lg would letterbox a page of A4 into a column.
        className="flex h-[90vh] max-w-[min(96vw,1100px)] flex-col gap-3 p-4 sm:max-w-[min(96vw,1100px)]"
      >
        <div className="flex min-w-0 items-center justify-between gap-3 pr-8">
          <div className="min-w-0">
            <DialogTitle className="truncate text-sm font-medium">
              {attachment.filename}
            </DialogTitle>
            <DialogDescription className="text-xs">
              {attachment.mime_type}
            </DialogDescription>
          </div>
          {/* The way out, for printing, downloading, or a second monitor —
              the behaviour this preview replaced, kept as a deliberate choice
              rather than removed. */}
          <a
            href={attachmentUrl(attachment.id)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Open in new tab
          </a>
        </div>

        <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-lg border border-border bg-muted">
          {failed ? (
            <p className="p-6 text-sm text-muted-foreground">
              This file is no longer available.
            </p>
          ) : !url ? (
            <p className="p-6 text-sm text-muted-foreground">Loading…</p>
          ) : isImage ? (
            <img
              src={url}
              alt={attachment.filename}
              className="max-h-full max-w-full object-contain"
            />
          ) : (
            <iframe
              src={documentPreviewUrl(url)}
              title={attachment.filename}
              className="h-full w-full border-0 bg-background"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
